import { Divider } from "antd";
import React, { useEffect, useState } from "react";
import { UserOutlined } from "@ant-design/icons";
import { formatCurrency } from "../services/services";

const regionColors: Record<string, string[]> = {
  // REGION: [BG COLOR, TEXT COLOR, HIGHTLIGHT COLOR]
  IDC: ["#fdf8ef", "#73381b", "#b3521b"],
  LATAM: ["#f0fdfa", "#134e4a", "#0f766e"],
  USCA: ["#f1f4fc", "#313777", "#485bc8"],
};

function extractUpToFiveKeys<T>(obj: Record<string, T>): any {
  const keys = Object.keys(obj).slice(0, 5); // take first 4 (or less)
  const result: Record<string, T> = {};

  for (const key of keys) {
    result[key] = obj[key];
  }

  return result;
}

const hasFilter = (
  serviceLineFilter: any,
  regionCode: string,
  serviceLineCode: string
): boolean => {
  // Check if the regionCode exists and if the serviceLineCode exists for that region
  return serviceLineFilter[regionCode]?.includes(serviceLineCode) ?? false;
};

export default function ServiceLineSummaryByRegion({
  regionName,
  slData,
  serviceLineFilter,
  setServiceLineFilter,
}: {
  regionName: string;
  slData: any;
  serviceLineFilter: any;
  setServiceLineFilter: any;
}) {
  const colorPalette: string[] = regionColors[regionName] ?? [
    "#f3f7fc",
    "#214159",
    "#255a7f",
  ];

  const [displayData, setDisplayData] = useState(
    extractUpToFiveKeys(slData.resource_service_lines)
  );
  const [showMore, setShowMore] = useState(false);

  useEffect(() => {
    setDisplayData(extractUpToFiveKeys(slData.resource_service_lines));
  }, [slData, regionName]);

  const toggleFilter = (regionCode: string, serviceLineCode: string): void => {
    setServiceLineFilter((prevFilter: any) => {
      const currentFilter = prevFilter[regionCode] || [];

      // If the serviceLineCode exists, remove it, otherwise add it
      if (currentFilter.includes(serviceLineCode)) {
        // Remove the serviceLineCode
        const updatedFilter = currentFilter.filter(
          (code: string) => code !== serviceLineCode
        );
        // If the list is empty after removal, remove the region from the filterObj
        if (updatedFilter.length === 0) {
          const { [regionCode]: removed, ...rest } = prevFilter;
          return rest; // Remove the region from filterObj
        }
        return {
          ...prevFilter,
          [regionCode]: updatedFilter,
        };
      } else {
        // Add the serviceLineCode if it doesn't exist
        return {
          ...prevFilter,
          [regionCode]: [...currentFilter, serviceLineCode], // Add the new serviceLineCode
        };
      }
    });
  };

  const SubCard = ({
    slName,
    revenue,
    count,
    region,
  }: {
    slName: string;
    revenue: string;
    count: string;
    region: string;
  }) => {
    const [selected, setSelected] = useState(
      hasFilter(serviceLineFilter, region, slName)
    );

    return (
      <>
        <style>{`
        .sl-row:hover {
            scale: 1.05;
            cursor: pointer;
            transition: all 0.2s ease-in-out;
        }`}</style>
        <div
          style={{
            borderLeft: `3px solid ${colorPalette[2]}`,
            padding: "1px 4px 1px 8px",
            borderRadius: "3px",
            color: selected ? "white" : "",
            backgroundColor: selected ? colorPalette[2] : "",
            scale: selected ? "1.05" : "",
          }}
          className="sl-row"
          onClick={() => {
            toggleFilter(region, slName);
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div style={{ display: "flex", flexDirection: "column" }}>
              <div>{slName}</div>
              <div style={{ color: selected ? colorPalette[0] : "grey" }}>
                <UserOutlined
                  style={{ color: selected ? colorPalette[0] : "grey" }}
                />{" "}
                {count}
              </div>
            </div>
            <div>{formatCurrency(Number(revenue))}</div>
          </div>
        </div>
      </>
    );
  };

  return (
    <div
      style={{
        flex: 1,
        height: "100%",
        border: "1px solid #f0f0f0",
        borderRadius: "8px",
        padding: "12px",
        backgroundColor: colorPalette[0],
        color: colorPalette[1],
      }}
    >
      <style>{`
        .custom-scroll-y::-webkit-scrollbar {
            width: 0.3em;
            background-color: inherit;
        }
        .custom-scroll-y::-webkit-scrollbar-thumb {
            background-color:grey;
            border-radius: 12px;
        }
    `}</style>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div>
          <div style={{ fontWeight: "bold", fontSize: "16px" }}>{regionName}</div>
          <div><UserOutlined /> {slData.total_region_count}</div>
        </div>
        <div
          style={{
            //   fontWeight: "bold",
            fontSize: "16px",
            backgroundColor: colorPalette[2],
            borderRadius: "8px",
            padding: "6px 9px 4px 9px",
            color: "white",
            maxWidth: "65%",
            display: "flex",
            justifyContent: "center",
          }}
        >
          {formatCurrency(Number(slData.total_region_revenue))}
        </div>
      </div>
      <Divider />
      <div
        className="custom-scroll-y"
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "6px",
          // overflow: "auto",
        }}
      >
        {Object.keys(displayData).map((key) => {
          return (
            <SubCard
              key={key}
              slName={key}
              revenue={displayData[key].revenue}
              count={displayData[key].count}
              region={regionName}
            />
          );
        })}
        {!showMore && Object.keys(slData.resource_service_lines).length > 5 && (
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              cursor: "pointer",
            }}
            onClick={() => {
              setShowMore(true);
              setDisplayData(slData.resource_service_lines);
            }}
          >
            ... More
          </div>
        )}
        {showMore && (
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              cursor: "pointer",
            }}
            onClick={() => {
              setShowMore(false);
              setDisplayData(
                extractUpToFiveKeys(slData.resource_service_lines)
              );
            }}
          >
            Show Less ...
          </div>
        )}
      </div>
    </div>
  );
}
